import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import app from '../../main' // import the instance
import checkResponse from '@/utils/checkResponse'
import parserFilters from '@/utils/parserFilters'
import parserSlug from '@/utils/parserSlug'

Vue.use(Vuex)

const state = {
  commissions: [],
  commissionsClosed: [],
  commissionsYear: [],
  commissionsSeller: [],
  salesSeller: [],
  topSalesCustomer: [],
  topSalesProduct: [],
  pagination: {
    currentPage: 1,
    perPage: 20,
    totalCommissions: 0,
    totalPages: 0
  },
  paginationClosed: {
    currentPage: 1,
    perPage: 20,
    totalCommissions: 0,
    totalPages: 0
  },
  commissionsCatalog: {
    users: []
  },
  filterParams: '',
  filterParamsClosed: '',
  isModalVisible: false,
  isModalNoteVisible: false,
  commissionsTotal: 0,
  totalPaid: 0,
  commissionsTotalClosed: 0,
  totalPaidClosed: 0
}

const mutations = {
  SET_COMMISSIONS (state, payload) {
    state.commissions = payload.data

    state.pagination = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalCommissions: payload.total,
      totalPages: payload.last_page
    }
  },
  SET_COMMISSIONS_CLOSED (state, payload) {
    state.commissionsClosed = payload.data

    state.paginationClosed = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalCommissions: payload.total,
      totalPages: payload.last_page
    }
  },
  SET_COMMISSIONS_YEAR (state, payload) {
    state.commissionsYear = payload.data
  },
  SET_COMMISSIONS_SELLER (state, payload) {
    state.commissionsSeller = payload.data
  },
  SET_SALES_SELLER (state, payload) {
    state.salesSeller = payload.data
  },
  SET_TOP_SALES_CUSTOMER (state, payload) {
    state.topSalesCustomer = payload.data
  },
  SET_TOP_SALES_PRODUCT (state, payload) {
    state.topSalesProduct = payload.data
  },
  ADD_COMMISSION (state, payload) {
    state.commissions.unshift(payload)
  },
  UPDATE_COMMISSION (state, payload) {
    const idx = state.commissions.findIndex(u => u.id === payload.id)
    if (idx >= 0) {
      Vue.set(state.commissions, idx, payload)
    }
  },
  DELETE_COMMISSION (state, payload) {
    const idx = state.commissions.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.delete(state.commissions, idx)
    }
  },
  DELETE_COMMISSIONS (state, payload) {
    for (let id of payload.ids) {
      const idx = state.commissions.findIndex(u => u.id === id)

      if (idx >= 0) {
        Vue.delete(state.commissions, idx)
      }
    }
  },
  SET_MODAL_VISIBLE (state, payload) {
    state.isModalVisible = payload
  },
  SET_MODAL_NOTE_VISIBLE (state, payload) {
    state.isModalNoteVisible = payload
  },
  SET_FILTER_PARAMS (state, payload) {
    state.filterParams = payload
  },
  SET_FILTER_PARAMS_CLOSED (state, payload) {
    state.filterParamsClosed = payload
  },
  SET_CATALOG (state, { object, payload }) {
    state.commissionsCatalog[object] = payload.data
  },
  SET_COMMISSIONS_TOTAL (state, payload) {
    state.commissionsTotal = payload
  },
  SET_TOTAL_PAID (state, payload) {
    state.totalPaid = payload
  },
  SET_COMMISSIONS_TOTAL_CLOSED (state, payload) {
    state.commissionsTotalClosed = payload
  },
  SET_TOTAL_PAID_CLOSED (state, payload) {
    state.totalPaidClosed = payload
  }
}

const actions = {
  async loadCommissions ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`commissions?page=${payload.page}${state.filterParams}&status!=closed`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_COMMISSIONS', response.data.data)
        commit('SET_COMMISSIONS_TOTAL', response.data.commissions_total)
        commit('SET_TOTAL_PAID', response.data.total_paid)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadCommissionsClosed ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`commissions?page=${payload.page}${state.filterParamsClosed}&status=closed`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_COMMISSIONS_CLOSED', response.data.data)
        commit('SET_COMMISSIONS_TOTAL_CLOSED', response.data.commissions_total)
        commit('SET_TOTAL_PAID_CLOSED', response.data.total_paid)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async actionCommission ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`commissions/${payload.id}/${payload.action}`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_COMMISSION', response.data)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.commissions', text: `commissions.${payload.action}`, icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async acceptCommissions ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`commissions/accept`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.commissions', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async rejectCommissions ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`commissions/reject`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.commissions', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async propouseCommissions ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`commissions/propouse`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.commissions', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async closeCommissions ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`commissions/close`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.commissions', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  printReport ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      window.open(axios.defaults.baseURL + '/commissions/export?status!=closed' + state.filterParams)
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  printReportClosed ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      window.open(axios.defaults.baseURL + '/commissions/export?status=closed' + state.filterParamsClosed)
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadCommissionsYear ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await Vue.axios.get(`dashboard/commissionsyear?year=${payload.year}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_COMMISSIONS_YEAR', response)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadCommissionsSeller ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await Vue.axios.get(`dashboard/commissionsseller?month=${payload.month}&year=${payload.year}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_COMMISSIONS_SELLER', response)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadSalesSeller ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await Vue.axios.get(`dashboard/salesseller?month=${payload.month}&year=${payload.year}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_SALES_SELLER', response)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadTopSalesCustomer ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await Vue.axios.get(`dashboard/topsalescustomer?id=${payload.id}&year=${payload.year}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TOP_SALES_CUSTOMER', response)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadTopSalesProduct ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await Vue.axios.get(`dashboard/topsalesproduct?id=${payload.id}&year=${payload.year}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TOP_SALES_PRODUCT', response)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  applyFilter ({ commit }, payload) {
    const filter = parserFilters(payload)
    commit('SET_FILTER_PARAMS', filter)
  },
  clearFilter ({ commit }, payload) {
    const filter = ''
    commit('SET_FILTER_PARAMS', filter)
  },
  applyFilterClosed ({ commit }, payload) {
    const filter = parserFilters(payload)
    commit('SET_FILTER_PARAMS_CLOSED', filter)
  },
  clearFilterClosed ({ commit }, payload) {
    const filter = ''
    commit('SET_FILTER_PARAMS_CLOSED', filter)
  },
  async loadCatalog ({ commit }, payload) {
    let url = parserSlug(payload.url)
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`${url}${payload.filter}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_CATALOG', { object: payload.url, payload: response.data })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  setModalVisible ({ commit }, payload) {
    commit('SET_MODAL_VISIBLE', payload)
  },
  setModalNoteVisible ({ commit }, payload) {
    commit('SET_MODAL_NOTE_VISIBLE', payload)
  },
  setModalImportVisible ({ commit }, payload) {
    commit('SET_MODAL_IMPORT_VISIBLE', payload, { root: true })
  }
}

const getters = {
  commissions: state => state.commissions,
  commissionsClosed: state => state.commissionsClosed,
  commissionsCatalog: state => state.commissionsCatalog,
  commissionsYear: state => state.commissionsYear,
  commissionsSeller: state => state.commissionsSeller,
  salesSeller: state => state.salesSeller,
  topSalesCustomer: state => state.topSalesCustomer,
  topSalesProduct: state => state.topSalesProduct,
  pagination: state => state.pagination,
  paginationClosed: state => state.paginationClosed,
  isModalVisible: state => state.isModalVisible,
  isModalNoteVisible: state => state.isModalNoteVisible,
  filterParams: state => state.filterParams,
  filterParamsClosed: state => state.filterParamsClosed,
  commissionsTotal: state => state.commissionsTotal,
  totalPaid: state => state.totalPaid,
  commissionsTotalClosed: state => state.commissionsTotalClosed,
  totalPaidClosed: state => state.totalPaidClosed
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
